import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from './api';
import type { ThemePreference } from './types';

export const THEME_STORAGE_KEY = 'cop.theme';

interface ThemeValue {
  preference: ThemePreference;
  resolved: 'light' | 'dark';
  setPreference: (p: ThemePreference, opts?: { persistRemote?: boolean }) => void;
}

const ThemeCtx = createContext<ThemeValue | null>(null);

function readStored(): ThemePreference | null {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : null;
  } catch {
    return null;
  }
}

function systemPrefersDark() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

function apply(resolved: 'light' | 'dark', pref: ThemePreference) {
  const root = document.documentElement;
  root.dataset.theme = resolved;
  root.dataset.themePref = pref;
  root.style.colorScheme = resolved;
  document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', resolved === 'dark' ? '#202124' : '#FFFFFF'));
}

export function ThemeProvider({ children, signedIn }: { children: ReactNode; signedIn: boolean }) {
  const [preference, setPref] = useState<ThemePreference>(() => readStored() ?? 'system');
  const [systemDark, setSystemDark] = useState<boolean>(() => !!systemPrefersDark());
  const signedInRef = useRef(signedIn);
  signedInRef.current = signedIn;

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const resolved: 'light' | 'dark' = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  useEffect(() => {
    apply(resolved, preference);
  }, [resolved, preference]);

  const setPreference = useCallback((p: ThemePreference, opts: { persistRemote?: boolean } = {}) => {
    setPref(p);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, p);
    } catch {
      /* storage unavailable — theme still applies for this tab */
    }
    if (opts.persistRemote !== false && signedInRef.current) {
      api.patch('/me/preferences', { themePreference: p }).catch(() => undefined);
    }
  }, []);

  const value = useMemo(() => ({ preference, resolved, setPreference }), [preference, resolved, setPreference]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme(): ThemeValue {
  const v = useContext(ThemeCtx);
  if (!v) throw new Error('useTheme must be used inside <ThemeProvider>');
  return v;
}

/** Adopts the account's saved preference once per sign-in when this device has no explicit choice. */
export function useAdoptServerTheme(serverPref: ThemePreference | undefined) {
  const { setPreference } = useTheme();
  const adopted = useRef(false);
  useEffect(() => {
    if (!serverPref || adopted.current) return;
    adopted.current = true;
    const local = readStored();
    if (!local) setPreference(serverPref, { persistRemote: false });
  }, [serverPref, setPreference]);
}
